// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { TagManagerEditor } from './tag-manager-editor';
import { AssignTagsEditor } from './assign-tags-editor';
import { TagItem } from './tag-item';
import { type Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { TagSegment } from './tag-segment';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';
import { type MyInvestments } from '@shared/data-access/interfaces/common/my-investment';
import { type AggregatorHoldingPosition } from '@shared/data-access/interfaces/api/brokerage/aggregator';
import type {
  SingleTickerTag,
  TickersTags,
} from '@shared/data-access/interfaces/api/engine/citec';
import { findNestedSubRowsProp } from '@shared/ui-kit/utils/find-nested-subrows-prop';
import { useCallback, useMemo, useState } from 'react';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { CloseButton } from '@shared/ui-kit/components/dynamic-dialog';
import { useTranslation } from 'react-i18next';
import { type TagsSuggestions } from '../../types';

export type DataSource =
  | WatchListPosition[]
  | MyInvestments[]
  | AggregatorHoldingPosition[];

export interface ManageTagsProps {
  tags: Tag[];
  tickerTags: Array<{
    ticker: string;
    name: string;
    assetClass?: string;
    isin?: string | null;
    tags: string[];
    url_logo?: string | null;
  }>;
  suggestions: TagsSuggestions[];
  onCreateTag: (
    tag: string,
    includeTickers: boolean | undefined,
    addDraftTag: (tag: string, tickers: string[]) => Promise<void>,
    selectedSuggestion?: TagsSuggestions,
  ) => Promise<void>;
  onAddTicker?: (ticker: string, assetType: string) => void;
  onCreateTickerTag: (
    initialGroups: Tag[],
    updatedGroups: Tag[],
    initialTickers: TickersTags[],
    updatedTickers: TickersTags[],
    deletedTags: string[],
    deletedTickerTags: SingleTickerTag[],
    onClose: () => void,
    content?: string,
  ) => Promise<void>;
  dataSource: DataSource;
  assetsInput?: boolean;
  onClose: () => void;
}

export const ManageTags = ({
  tags,
  tickerTags,
  onCreateTag,
  onCreateTickerTag,
  suggestions,
  assetsInput,
  onAddTicker,
  dataSource,
  onClose,
}: ManageTagsProps) => {
  const { t } = useTranslation();
  const [draftTags, setDraftTags] = useState(tags);
  const [draftTickerTags, setDraftTickerTags] = useState(tickerTags);
  const deletedTags: string[] = useMemo(() => [], []);
  const deletedTickerTags: SingleTickerTag[] = useMemo(() => [], []);
  /* istanbul ignore next */
  const updateDraftTickerTags = useCallback(
    (tickers: string[], tag: string) => {
      const updatedDraftTickerTags = draftTickerTags.map(tickerTag => {
        if (tickers.includes(tickerTag.ticker)) {
          return {
            ...tickerTag,
            tags: [...tickerTag.tags, tag],
          };
        }
        return tickerTag;
      });

      setDraftTickerTags(updatedDraftTickerTags);
    },
    [draftTickerTags],
  );
  /* istanbul ignore next */
  const addDraftTag = useCallback(
    async (tag: string, tickers: string[]) => {
      const isTagExists = draftTags.some(t => t.name === tag);
      if (isTagExists) return;

      setDraftTags(prevTags => {
        const updatedTags = [...prevTags, { name: tag, tickers: tickers }];
        return updatedTags.sort((a, b) => a.name.localeCompare(b.name));
      });

      updateDraftTickerTags(tickers, tag);

      if (deletedTags.includes(tag)) {
        deletedTags.splice(deletedTags.indexOf(tag), 1);
      }
    },
    [draftTags, updateDraftTickerTags, deletedTags],
  );
  /* istanbul ignore next */
  const removeDraftTag = useCallback(
    async (tag: string) => {
      setDraftTags(prevTags => prevTags.filter(t => t.name !== tag));
      setDraftTickerTags(prevTags =>
        prevTags.map(tickerTag => {
          const updatedTags = tickerTag.tags.filter(t => t !== tag);
          return { ...tickerTag, tags: updatedTags };
        }),
      );

      deletedTags.push(tag);
    },
    [deletedTags],
  );
  /* istanbul ignore next */
  const removeDraftTickerTag = useCallback(
    async (tag: string, ticker: string) => {
      const updatedDraftTickerTags = draftTickerTags.map(tickerTag => {
        if (tickerTag.tags.includes(tag) && tickerTag.ticker === ticker) {
          return {
            ...tickerTag,
            tags: tickerTag.tags.filter(t => t !== tag),
          };
        }
        return tickerTag;
      });

      setDraftTickerTags(updatedDraftTickerTags);

      deletedTickerTags.push({ tag, ticker });
    },
    [draftTickerTags, deletedTickerTags],
  );

  const isDisabled = useMemo(
    () =>
      JSON.stringify(tags) === JSON.stringify(draftTags) &&
      JSON.stringify(tickerTags) === JSON.stringify(draftTickerTags),
    [tags, draftTags, tickerTags, draftTickerTags],
  );
  /* istanbul ignore next */
  const saveTickerTagChanges = useCallback(
    async (content?: string) => {
      if (isDisabled) return onClose();

      onCreateTickerTag(
        tags,
        draftTags,
        tickerTags,
        draftTickerTags,
        deletedTags,
        deletedTickerTags,
        onClose,
        content,
      );
    },
    [
      tags,
      draftTags,
      tickerTags,
      draftTickerTags,
      onCreateTickerTag,
      onClose,
      deletedTags,
      deletedTickerTags,
      isDisabled,
    ],
  );

  return (
    <>
      <div className="flex justify-end">
        <IconButton
          className="mr-6"
          icon="Save"
          variant="button"
          disabled={isDisabled}
          onClick={() => saveTickerTagChanges()}
        />
        <CloseButton
          onClick={() =>
            saveTickerTagChanges(
              t('tag-manager.unsaved-changes-warning', { ns: 'components' }),
            )
          }
        />
      </div>
      <TagManagerEditor
        onTag={onCreateTag}
        suggestions={suggestions}
        addDraftTag={addDraftTag}
      >
        {Array.from(draftTags, ({ name }, key) => (
          <TagItem
            key={`${name}-${key}`}
            label={name}
            tag={name}
            onDelete={removeDraftTag}
          />
        ))}
      </TagManagerEditor>

      <hr />

      <AssignTagsEditor assetsInput={assetsInput} onAddTicker={onAddTicker}>
        {Array.from(
          draftTickerTags,
          ({ ticker, assetClass, isin, name, url_logo, tags: selectedTags }) => (
            <TagSegment
              key={ticker}
              ticker={ticker}
              name={name}
              isin={isin}
              assetClass={assetClass}
              logo={
                findNestedSubRowsProp(ticker, dataSource, 'url_logo') ?? url_logo
              }
              assetType={findNestedSubRowsProp(ticker, dataSource, 'asset_type')}
              options={draftTags}
              selectedTags={selectedTags}
              onTickerTag={updateDraftTickerTags}
              onTickerTagDelete={removeDraftTickerTag}
            />
          ),
        )}
      </AssignTagsEditor>
    </>
  );
};
